import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useBamAuth } from '@/auth';
import { AdvancedSearchData } from '@/components/search/types';
import { searchCriteriaForEenheden, searchCriteriaForIds } from '@/components/search/v1/utils/helpers';
import useSidebarStore, { SidebarStore } from '@/components/sidebar/hooks/useSidebarStore';
import { mapCriteriaVoorSignalenToevoegen } from '@/components/sidebar/SignalenLijstHelper';
import useClientState from '@/state/useClientState';

import useMutatieWissel from './useMutatieWissel';
import useMwdAction from './useMwdAction';
import useMwdActions from './useMwdActions';

/**
 * Execute the general shortkeys to execute an action.
 * A shortkey is 'general' when you can execute without
 * any particular state selection of domain objects.
 *
 * Alt+F - Open mwd filter (advanced search dialog).
 *
 * Alt+L - Verwijder alle werklijnen uit het Mwd.
 *
 *
 */
type GeneralShortkeysProps = {
  isAllowedToMutate?: boolean;
  isAllowedToView?: boolean;
  handleSelectieUitPanel: (advancedSearchData: AdvancedSearchData[]) => void;
};
const useGeneralShortkeys = ({ handleSelectieUitPanel }: GeneralShortkeysProps) => {
  const { hasPermission } = useBamAuth();
  const isAllowedToMutate = hasPermission('mwd.mutate');
  const isAllowedToView = hasPermission('mwd.view');
  const setShowAdvancedSearchDialog = useClientState((state) => state.setShowAdvancedSearchDialog);
  const hideDetailPanel = useClientState((state) => state.hideDetailPanel);
  const { verwijderWerklijnen, gekoppeldeWerklijnenTonen } = useMwdActions();
  const { geslotenWissel, openWissel } = useMutatieWissel();
  const { executeAction } = useMwdAction();
  const location = useLocation();
  const isMwdV2 = location.pathname.endsWith('/');
  const sidebarStore = useSidebarStore;

  const keydownToAction = (keyCode: string, isAllowedToMutate: boolean) => {
    const store = sidebarStore.getState();
    if (keyCode === 'KeyF') setShowAdvancedSearchDialog(true);
    if (keyCode === 'KeyL') {
      void executeAction(verwijderWerklijnen);
      hideDetailPanel();
    }
    if (keyCode === 'KeyT') {
      if (store.activePanel !== undefined) {
        const handlers: Record<NonNullable<SidebarStore['activePanel']>, () => void> = {
          Signalen: () => {
            if (store.signalen.length > 0) {
              handleSelectieUitPanel(mapCriteriaVoorSignalenToevoegen(store.signalen));
            }
          },
          Conflicten: () => {
            if (store.conflicten.length > 0) {
              handleSelectieUitPanel([searchCriteriaForIds(store.conflicten.map((c) => c.werklijnId))]);
            }
          },
          Defecten: () => {
            if (store.defecten.length > 0) {
              const eenheden = [...new Set(store.defecten.map((d) => d.eenheidNummer))];
              handleSelectieUitPanel([searchCriteriaForEenheden(eenheden)]);
            }
          },
          Mwd: () => {
            // Geen actie aan verbonden, en wordt alleen gebruikt voor highlighting
          },
        };
        handlers[store.activePanel]?.();
        store.reset();
      } else {
        void gekoppeldeWerklijnenTonen();
      }
    }
    if (!isAllowedToMutate) return;
    // There can be a case made to placing domain specific objects here
    // They need selected Overgang(en) and are protected by mutation rights.
    // BUT its the most rendered Component.
    if (keyCode === 'KeyW') void executeAction(geslotenWissel);
    if (keyCode === 'KeyO') void executeAction(openWissel);
  };

  useEffect(() => {
    /**
     * Vanilla keydown-capture on the window object.
     * @param event
     */
    const listen = (event: KeyboardEvent) => {
      if (isMwdV2 && isAllowedToView && event.altKey && ['KeyF', 'KeyL', 'KeyW', 'KeyO', 'KeyT'].includes(event.code)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        keydownToAction(event.code, isAllowedToMutate);
      }
    };
    window.addEventListener('keydown', listen, { capture: true });

    return () => {
      window.removeEventListener('keydown', listen, { capture: true });
    };
  }, []);
};
export default useGeneralShortkeys;
